/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { sortBy } from 'lodash';
import React, { ChangeEvent, memo, useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import {
  AutocompleteRenderInputParams,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
} from '@mui/material';

import { DefaultClusterLimits } from 'shared';

import { AlignCenter } from '../../../../components/AlignCenter/AlignCenter';
import { ArrowRoundedIcon } from '../../../../components/svg-icons/ArrowRoundedIcon';
import { HUAutocomplete } from '../../../../elements/HUAutocomplete/HUAutocomplete';
import { UnitsToBytesMultiplier } from '../../../../elements/HUBytesInput/components/UnitPicker.types';
import { HUBytesInput } from '../../../../elements/HUBytesInput/HUBytesInput';
import { destructRenderInputParams } from '../../../../elements/HUTextInput/utils';
import { Namespaces } from '../../../../i18n';
import { useFormStyles } from '../../../../styles/form';
import { useSupportPlansStyles } from '../../../../styles/supportPlans';
import { PlanCompute } from '../../../../types/common/PlanCompute';
import { SystemConfiguration } from '../../../../types/superuser/SystemConfiguration';
import { fetchSystemConfiguration } from '../../../../utils/queryFns';
import { CreateClusterValues } from '../../CreateClusterOverlay.types';
import { AllocationContext, PlansContext } from './consts';
import { FormProps, params, TextFieldProps } from './types';

export const DynamicAllocation = memo(
  <Values extends CreateClusterValues>({
    values,
    setFieldValue,
    plan: { planCompute, planStorage },
    minStorageSize = 0,
    handleChange,
    touched,
    errors,
    setFieldTouched,
    storageClasses,
  }: FormProps<Values>) => {
    const classes = useSupportPlansStyles();
    const formClasses = useFormStyles();
    const { t } = useTranslation([Namespaces.Common, Namespaces.CreateCluster]);

    const { apiErrors, resetApiError } = useContext(AllocationContext);

    const { data, isLoading, isError } = useQuery([fetchSystemConfiguration.key], fetchSystemConfiguration.fn, {
      refetchOnMount: true,
    });

    const config = useMemo(() => data?.payload ?? ({} as SystemConfiguration), [data]);

    const storageClassesOptions = useMemo(() => sortBy(storageClasses, ['id']).map(sc => sc.id), [storageClasses]);

    const { selectedPlans, setSelectedPlans } = useContext(PlansContext);
    const { computeId, storageId } = selectedPlans;

    const shouldRender = useMemo(() => !isLoading && !isError && !!config, [config, isError, isLoading]);

    const hasError = (field: keyof CreateClusterValues, skipTouched: boolean = false) => {
      return (skipTouched || touched[field]) && !!(errors[field] || apiErrors[field]);
    };

    const getError = (field: keyof CreateClusterValues) => {
      const error = errors[field] || apiErrors[field];
      return error ? String(error) : null;
    };

    const [minMemoryLimit, maxMemoryLimit] = useMemo(
      () => [
        config.minMemoryLimit ?? DefaultClusterLimits.MemoryGbs.Min,
        config.maxMemoryLimit ?? DefaultClusterLimits.MemoryGbs.Max,
      ],
      [config.minMemoryLimit, config.maxMemoryLimit],
    );

    const [minStorageLimit, maxStorageLimit] = useMemo(
      () => [
        config.minStorageLimit ?? DefaultClusterLimits.StorageGbs.Min,
        config.maxStorageLimit ?? DefaultClusterLimits.StorageGbs.Max,
      ],
      [config.minStorageLimit, config.maxStorageLimit],
    );

    const getTranslationLabel = useCallback(
      (name: (typeof params)[number]) =>
        name === 'cpuUnits'
          ? `${Namespaces.CreateCluster}:labels.dynamic_allocation.cpu_number`
          : name === 'memoryGbs'
            ? `${Namespaces.CreateCluster}:labels.dynamic_allocation.memory_gbs`
            : `${Namespaces.CreateCluster}:labels.dynamic_allocation.storage_gbs`,
      [],
    );

    const handleStorageClassChange = useCallback(
      (value: string) => {
        void setFieldValue('storageClassId', value);
      },
      [setFieldValue],
    );

    const renderStorageClassOption = (props: React.HTMLAttributes<HTMLLIElement> & { key: any }, value: string) => {
      const storageClass = storageClasses.find(sc => sc.id === value);
      return storageClass ? (
        <li {...props}>
          {storageClass.id}
          {storageClass.isDefault ? ` (${t(`${Namespaces.Common}:label.default`)})` : ''}
        </li>
      ) : null;
    };

    const handleValueChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        e.persist();
        handleChange(e);
        resetApiError(e.target.name);
        void setFieldTouched(e.target.name, true, false);
      },
      [handleChange, resetApiError, setFieldTouched],
    );

    const generateTextFieldProps = useCallback(
      ({ name, min, value }: TextFieldProps) => ({
        name,
        id: name,
        onChange: handleValueChange,
        value: value,
        inputProps: {
          min: min ?? 1,
        },
        type: 'number' as const,
        fullWidth: true,
      }),
      [handleValueChange],
    );

    const handleSelectComputePlan = useCallback(
      (plan: PlanCompute) => {
        setSelectedPlans({ computeId: plan.id });
      },
      [setSelectedPlans],
    );

    const preparedComputePlans = useMemo(() => sortBy(planCompute, ['cpu', 'memory']), [planCompute]);

    const preparedStoragePlans = useMemo(() => sortBy(planStorage, ['cpu', 'memory']), [planStorage]);

    useEffect(() => {
      if (preparedComputePlans) {
        const selectedPlan = preparedComputePlans.find(p => p.id === computeId);
        if (!selectedPlan) {
          const defaultPlan = preparedComputePlans.find(p => p.isDefault) || preparedComputePlans[0];
          if (defaultPlan) {
            handleSelectComputePlan(defaultPlan);
          }
        }
      }
    }, [preparedComputePlans, setFieldValue, computeId, handleSelectComputePlan]);

    useEffect(() => {
      if (preparedStoragePlans) {
        const activePlan = preparedStoragePlans.find(p => p.id === storageId);
        if (!activePlan || activePlan.size < minStorageSize) {
          const filteredPlans = preparedStoragePlans.filter(p => p.size >= minStorageSize);
          const defaultPlan = filteredPlans.find(p => p.isDefault) || filteredPlans[0];
          defaultPlan && setSelectedPlans({ storageId: defaultPlan.id });
        }
      }
    }, [preparedStoragePlans, setSelectedPlans, storageId, minStorageSize]);

    const renderAutocompleteInput = (renderParams: AutocompleteRenderInputParams) => (
      <OutlinedInput
        {...destructRenderInputParams(renderParams)}
        id="storageClassId"
        name="storageClassId"
        label={t(`${Namespaces.CreateCluster}:labels.dynamic_allocation.storage_class`)}
      />
    );

    const handleBytesChange = (field: 'memoryGbs' | 'storageGbs', bytes: string) => {
      const value = bytes ? Number(bytes) / UnitsToBytesMultiplier['GB'] : bytes;
      void setFieldValue(field, value);
      void setFieldTouched(field, true, false);
    };

    return shouldRender ? (
      <Box className={cx(classes.root)}>
        <Grid item classes={{ root: 'd-flex' }}>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthDefault, classes.formControl)}
            error={hasError('cpuUnits')}
            variant="outlined"
          >
            <InputLabel htmlFor="cpuUnits">{t(getTranslationLabel('cpuUnits'))}</InputLabel>
            <OutlinedInput
              {...generateTextFieldProps({
                name: 'cpuUnits',
                value: values.cpuUnits,
                min: config.minCpuLimit ?? DefaultClusterLimits.Cpu.Min,
              })}
              label={t(getTranslationLabel('cpuUnits'))}
              autoFocus
            />
            {hasError('cpuUnits') && <FormHelperText>{getError('cpuUnits')}</FormHelperText>}
          </FormControl>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthDefault, classes.formControl)}
            error={hasError('memoryGbs')}
            variant="outlined"
          >
            <InputLabel htmlFor="memoryGbs">{t(getTranslationLabel('memoryGbs'))}</InputLabel>
            <HUBytesInput
              bytes={values.memoryGbs * UnitsToBytesMultiplier['GB']}
              onChange={bytes => handleBytesChange('memoryGbs', bytes)}
              minBytes={minMemoryLimit * UnitsToBytesMultiplier['GB']}
              maxBytes={maxMemoryLimit * UnitsToBytesMultiplier['GB']}
              label={t(getTranslationLabel('memoryGbs'))}
              name="memoryGbs"
            />
            {hasError('memoryGbs') && <FormHelperText>{getError('memoryGbs')}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid item classes={{ root: 'd-flex' }} style={{ marginTop: '20px' }}>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthDefault, classes.formControl)}
            error={hasError('storageGbs')}
            variant="outlined"
          >
            <InputLabel htmlFor="storageGbs">{t(getTranslationLabel('storageGbs'))}</InputLabel>
            <HUBytesInput
              bytes={values.storageGbs * UnitsToBytesMultiplier['GB']}
              onChange={bytes => handleBytesChange('storageGbs', bytes)}
              minBytes={minStorageLimit * UnitsToBytesMultiplier['GB']}
              maxBytes={maxStorageLimit * UnitsToBytesMultiplier['GB']}
              label={t(getTranslationLabel('storageGbs'))}
              name="storageGbs"
            />
            {hasError('storageGbs') && <FormHelperText>{getError('storageGbs')}</FormHelperText>}
          </FormControl>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthDefault, classes.formControl)}
            variant="outlined"
          >
            <InputLabel htmlFor="storageClass">
              {t(`${Namespaces.CreateCluster}:labels.dynamic_allocation.storage_class`)}
            </InputLabel>
            <HUAutocomplete
              renderInput={renderAutocompleteInput}
              popupIcon={<ArrowRoundedIcon />}
              options={storageClassesOptions}
              disableClearable
              value={values.storageClassId}
              onChange={(_e, value) => handleStorageClassChange(value)}
              renderOption={renderStorageClassOption}
              disabled={!storageClasses.length}
            />
          </FormControl>
        </Grid>
      </Box>
    ) : (
      <AlignCenter className={classes.loaderContainer}>
        <CircularProgress color="primary" size={40} />
      </AlignCenter>
    );
  },
);
